/**
 * What the crew knows, Text (slice 6d spec §8.5; Paper A5d): the whole file
 * as Markdown, saved as a whole; and the bar under both views. A save that
 * ran into a newer brief shows it, and keeps what was typed to save over it.
 */
import { Button } from "@crew/ui/components/button";
import { Progress } from "@crew/ui/components/progress";
import { Textarea } from "@crew/ui/components/textarea";
import { cn } from "@crew/ui/lib/utils";
import { useState } from "react";
import { briefRefusal, useSaveBrief, type BriefFile } from "./brief-api";
import { BRIEF_MAX, lengthLine, tooLong } from "./brief";

/** The bar, "1,820 of 4,000 characters", and "Saved · version 14". */
export function BriefFooter({
  length,
  version,
  saving,
}: {
  length: number;
  version: number;
  saving: boolean;
}) {
  const over = length > BRIEF_MAX;
  return (
    <div className="flex items-center gap-3 text-meta text-muted-foreground" data-brief-footer>
      <Progress
        value={Math.min(100, (length / BRIEF_MAX) * 100)}
        aria-label={lengthLine(length)}
        className={cn(
          "w-[120px] shrink-0",
          over
            ? "[&_[data-slot=progress-indicator]]:bg-brick"
            : "[&_[data-slot=progress-indicator]]:bg-moss",
        )}
      />
      <span className={cn("tabular", over && "text-brick")} data-brief-length>
        {lengthLine(length)}
      </span>
      <span className="ml-auto" data-brief-version={version}>
        {saving ? "Saving…" : `Saved · version ${version}`}
      </span>
    </div>
  );
}

/** The draft (null until typed in: the file as it is, following live changes) and its save. */
function useTextDraft(areaId: string, file: BriefFile) {
  const save = useSaveBrief(areaId);
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? file.content;
  const long = tooLong(value.length);
  return {
    value,
    long,
    save,
    ...briefRefusal(save.error),
    dirty: draft !== null && draft !== file.content,
    edit: setDraft,
    discard: () => setDraft(null),
    // Typing during the save keeps the newer words as the draft.
    submit: () =>
      save.mutate(
        { content: value, version: file.version },
        { onSuccess: () => setDraft((d) => (d === value ? null : d)) },
      ),
  };
}

type TextDraft = ReturnType<typeof useTextDraft>;

export function TextView({ areaId, file }: { areaId: string; file: BriefFile }) {
  const text = useTextDraft(areaId, file);
  return (
    <div className="flex flex-col gap-5" data-brief-text>
      <FileBox text={text} />
      <TextProblems text={text} />
      <div className="flex items-center gap-4">
        <Button
          disabled={!text.dirty || Boolean(text.long) || text.save.isPending}
          onClick={text.submit}
        >
          Save
        </Button>
        {text.dirty ? (
          <Button variant="ghost" disabled={text.save.isPending} onClick={text.discard}>
            Discard
          </Button>
        ) : null}
      </div>
      <BriefFooter length={text.value.length} version={file.version} saving={text.save.isPending} />
    </div>
  );
}

function FileBox({ text }: { text: TextDraft }) {
  return (
    <div className="flex flex-col rounded-xl bg-card ring-1 ring-border">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border px-5 py-3">
        <span className="font-mono text-xs text-copy">AREA.md</span>
        <span className="text-xs text-muted-foreground">
          Markdown. Facts and notes as “- ” lines.
        </span>
      </div>
      <Textarea
        aria-label="AREA.md"
        value={text.value}
        spellCheck={false}
        onChange={(e) => text.edit(e.target.value)}
        aria-invalid={text.long ? true : undefined}
        className="h-[28rem] resize-y rounded-none border-0 px-5 py-4 font-mono text-[13px] leading-6 shadow-none [field-sizing:fixed] focus-visible:ring-0"
      />
    </div>
  );
}

/** A newer brief the save ran into, past 4,000, or another refusal. */
function TextProblems({ text }: { text: TextDraft }) {
  return (
    <>
      {text.conflict ? <ConflictNotice content={text.conflict.content} /> : null}
      {text.long ? <p className="text-meta text-brick">{text.long}</p> : null}
      {text.problem ? <p className="text-meta text-brick">{text.problem}</p> : null}
    </>
  );
}

function ConflictNotice({ content }: { content: string }) {
  return (
    <div
      role="alert"
      data-brief-conflict
      className="flex flex-col gap-2 rounded-xl border border-copper/40 bg-copper/5 p-3"
    >
      <p className="text-meta text-copy">
        Someone changed this since. Here's the brief as it is now. Save keeps yours over it.
      </p>
      <pre className="max-h-48 overflow-auto rounded-md bg-background px-3 py-2 font-mono text-xs leading-5 whitespace-pre-wrap text-foreground ring-1 ring-border">
        {content}
      </pre>
    </div>
  );
}
